import { isDevMode, setDevMode, toggleDevMode, onDevModeChange } from './devMode';
import { hotkeyRegistry } from './hotkeyRegistry';
import { isTextEntryTarget } from './keyboardTarget';

// Director mode — the master "hide the dev HUD for a clean viewer view"
// toggle, layered on devMode (Phase 1): devMode=true → dev HUD visible,
// devMode=false → director mode (no HUD).
//
// Every current dev surface (timeline, inspector, event log, gizmo overlays)
// hides ITSELF via an onDevModeChange subscription, so this module no longer
// needs the old body-class CSS that force-hid the standalone legacy panels —
// they've all been folded into the inspector + timeline and deleted. What
// remains here is the global D hotkey + a small floating "exit director" pill:
// the one affordance that survives in director mode so you can click back in.
//
// Initial wiring is done once at app startup; the hotkey + pill stay
// installed for the session.

export function initDirectorMode(): { dispose(): void } {
  // Return-to-dev pill. Shown only WHILE in director mode (devMode off) — in
  // dev mode the inspector header's "director" button is the entry point.
  // Bottom-left so it never collides with the top-left inspector.
  const pill = document.createElement('div');
  pill.id = 'lec-director-pill';
  Object.assign(pill.style, {
    position: 'fixed',
    bottom: '12px',
    left: '12px',
    zIndex: '50',
    background: 'rgba(18, 22, 28, 0.82)',
    color: '#66ffe6',
    font: '10px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    padding: '4px 10px',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.10)',
    cursor: 'pointer',
    userSelect: 'none',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  });
  pill.textContent = '◐ exit director (D)';
  function refreshPill(): void {
    pill.style.display = isDevMode() ? 'none' : 'block';
  }
  refreshPill();
  pill.addEventListener('click', () => setDevMode(true));
  const unsubPill = onDevModeChange(refreshPill);
  document.body.appendChild(pill);

  // Global D hotkey toggles director mode. Ignored inside text inputs.
  const onKey = (e: KeyboardEvent): void => {
    if (isTextEntryTarget(e)) return; // U2: also bail on focused <select>/contentEditable
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'd' || e.key === 'D') {
      e.preventDefault();
      toggleDevMode();
    }
  };
  window.addEventListener('keydown', onKey);
  const unregHotkey = hotkeyRegistry.register({
    keys: 'D', description: 'director mode (hide dev UI)', section: 'dev HUD', order: 1,
  });

  return {
    dispose(): void {
      unsubPill();
      window.removeEventListener('keydown', onKey);
      unregHotkey();
      pill.remove();
    },
  };
}

/** Explicit setters in case some caller wants to flip director mode
 *  without going through the hotkey/pill (e.g. on first load before the
 *  pill exists). */
export { setDevMode, toggleDevMode, isDevMode };
